export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  firstItemOnPage: number;
  lastItemOnPage: number;
}

export const TerritorialLevelNumber = {
  Pais: 0,
  Estado: 1,
  Municipio: 2,
  Localidad: 3,
  Colonia: 4,
} as const;

export interface CountryPaginationParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  isActive?: boolean;
  level?: TerritorialLevel;
  parentId?: string;
}

export const TerritorialLevel = {
  Pais: 'Pais',
  Estado: 'Estado',
  Municipio: 'Municipio',
  Localidad: 'Localidad',
  Colonia: 'Colonia',
} as const;

export type TerritorialLevel = (typeof TerritorialLevel)[keyof typeof TerritorialLevel];

export const mapLevelNumberToString = (level: number): TerritorialLevel => {
  switch (level) {
    case TerritorialLevelNumber.Pais:
      return TerritorialLevel.Pais;
    case TerritorialLevelNumber.Estado:
      return TerritorialLevel.Estado;
    case TerritorialLevelNumber.Municipio:
      return TerritorialLevel.Municipio;
    case TerritorialLevelNumber.Localidad:
      return TerritorialLevel.Localidad;
    case TerritorialLevelNumber.Colonia:
      return TerritorialLevel.Colonia;
    default:
      return TerritorialLevel.Pais;
  }
};

export const mapLevelStringToNumber = (level: TerritorialLevel): number => {
  switch (level) {
    case TerritorialLevel.Pais:
      return TerritorialLevelNumber.Pais;
    case TerritorialLevel.Estado:
      return TerritorialLevelNumber.Estado;
    case TerritorialLevel.Municipio:
      return TerritorialLevelNumber.Municipio;
    case TerritorialLevel.Localidad:
      return TerritorialLevelNumber.Localidad;
    case TerritorialLevel.Colonia:
      return TerritorialLevelNumber.Colonia;
  }
};

export interface Country {
  id: string;
  officialName: string;
  commonName: string;
  isoCode?: string;
  currency: string;
  level: TerritorialLevel;
  parentId: string | null;
  parentName?: string;
  isActive: boolean;
  createdAt?: string;
}

export interface CountryHierarchy extends Country {
  children: CountryHierarchy[];
}

export interface Region {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
}

export interface RegionType {
  id: string;
  name: string;
  regions: Region[];
}

export interface CountryDetail extends Country {
  parentName?: string;
  createdAt: string;
  regionTypes?: RegionType[];
}

export interface CreateCountryRequest {
  officialName: string;
  commonName: string;
  isoCode?: string;
  currencyCode: string;
  level: number;
  parentId: string | null;
}

export interface UpdateCountryRequest {
  officialName: string;
  commonName: string;
  currencyCode: string;
  level?: TerritorialLevel;
  parentId?: string | null;
}

export interface Currency {
  code: string;
  name: string;
  symbol: string;
}

export const CURRENCIES: Currency[] = [
  { code: 'MXN', name: 'Peso Mexicano', symbol: '$' },
  { code: 'USD', name: 'Dólar Estadounidense', symbol: 'US$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'COP', name: 'Peso Colombiano', symbol: '$' },
  { code: 'ARS', name: 'Peso Argentino', symbol: '$' },
  { code: 'CLP', name: 'Peso Chileno', symbol: '$' },
  { code: 'PEN', name: 'Sol Peruano', symbol: 'S/' },
  { code: 'VES', name: 'Bolívar Soberano', symbol: 'Bs' },
  { code: 'GTQ', name: 'Quetzal Guatemalteco', symbol: 'Q' },
  { code: 'HNL', name: 'Lempira Hondureña', symbol: 'L' },
  { code: 'NIO', name: 'Córdoba Nicaragüense', symbol: 'C$' },
  { code: 'CRC', name: 'Colón Costarricense', symbol: '₡' },
  { code: 'PAB', name: 'Balboa Panameño', symbol: 'B/.' },
  { code: 'DOP', name: 'Peso Dominicano', symbol: 'RD$' },
  { code: 'CUP', name: 'Peso Cubano', symbol: '$' },
  { code: 'BOB', name: 'Boliviano', symbol: 'Bs.' },
  { code: 'PYG', name: 'Guaraní Paraguayo', symbol: '₲' },
  { code: 'UYU', name: 'Peso Uruguayo', symbol: '$U' },
];
